import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Select,
  Textarea,
} from '../../../components/ui';
import { listTaxonomies, TaxonomyItem } from '../../../services/taxonomiesService';
import { userService } from '../../../services/userService';
import { userStorage } from '../../../utils/auth';
import { createIssue, issuesErrorMessage, updateIssue } from '../issuesApi';
import type { IssueStatus, IssueView } from '../../../types/issues';
import { ISSUE_STATUSES, PRIORITY_LABEL, readableKey, STATUS_LABEL } from './format';
import { PositionPicker } from './PositionMark';

export interface CategoryOption {
  value: string;
  label: string;
}

interface IssueFormDialogProps {
  /** Omitted when creating */
  issue?: IssueView;
  /** Categories already in use, offered as suggestions */
  categories?: CategoryOption[];
  onClose: () => void;
  onSaved: (issue: IssueView) => void;
}

interface Owner {
  id: string;
  name: string;
}

const nameOf = (user: { firstName?: string; lastName?: string; email?: string }): string =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || 'Unnamed';

/**
 * Compact form for what an issue is: the title, what is at stake, how it is
 * classified, how much it matters, what we want, when it is decided and who
 * is accountable for it.
 */
export const IssueFormDialog: React.FC<IssueFormDialogProps> = ({ issue, categories = [], onClose, onSaved }) => {
  const titleId = useId();
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const editing = !!issue;

  const [title, setTitle] = useState(issue?.title ?? '');
  const [summary, setSummary] = useState(issue?.summary ?? '');
  const [category, setCategory] = useState(issue?.category ?? '');
  const [options, setOptions] = useState<CategoryOption[]>(categories);
  const [status, setStatus] = useState<IssueStatus>(issue?.status ?? 'active');

  // The backend only accepts configured categories, so the field offers those
  // (plus anything already in use) rather than letting a typo be rejected
  useEffect(() => {
    let cancelled = false;
    listTaxonomies('issue_category')
      .then((items) => {
        if (cancelled) return;
        const fromTaxonomy: CategoryOption[] = items.map((item: TaxonomyItem) => ({
          value: item.key,
          label: item.label,
        }));
        const known = new Set(fromTaxonomy.map((option) => option.value));
        setOptions([
          ...fromTaxonomy,
          // A category an older issue carries that nobody configured: keep it
          // selectable, but show it in the same words as the rest
          ...categories
            .filter((option: CategoryOption) => !known.has(option.value))
            .map((option: CategoryOption) => ({
              value: option.value,
              label: option.label === option.value ? readableKey(option.value) : option.label,
            })),
        ]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // categories is a fresh array each render; its contents rarely change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [priority, setPriority] = useState(issue?.priority ?? 3);
  const [ourPosition, setOurPosition] = useState(issue?.ourPosition ?? 2);
  const [ourPositionNote, setOurPositionNote] = useState(issue?.ourPositionNote ?? '');
  const [decisionDate, setDecisionDate] = useState((issue?.decisionDate ?? '').slice(0, 10));
  const [ownerId, setOwnerId] = useState(issue?.owner?.id ?? '');
  const [owners, setOwners] = useState<Owner[]>(issue?.owner ? [issue.owner] : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Who can own an issue. Listing users is an admin endpoint, so fall back
  // quietly to the signed-in analyst rather than showing an empty picker.
  useEffect(() => {
    let cancelled = false;
    const me = userStorage.get();
    const fallback: Owner[] = me?.id ? [{ id: me.id, name: nameOf(me) }] : [];
    Promise.resolve(userService.getUsers({ limit: 100, isActive: true }))
      .then((response) => {
        if (cancelled) return;
        const list = (response?.users || []).map((user: any) => ({ id: user.id, name: nameOf(user) }));
        setOwners(list.length ? list : fallback);
      })
      .catch(() => {
        if (!cancelled) setOwners((current) => (current.length ? current : fallback));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ownerOptions = useMemo(() => {
    const seen = new Set<string>();
    const list: Owner[] = [];
    [...(issue?.owner ? [issue.owner] : []), ...owners].forEach((owner) => {
      if (!owner?.id || seen.has(owner.id)) return;
      seen.add(owner.id);
      list.push(owner);
    });
    return list;
  }, [owners, issue]);

  const save = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Give the issue a title');
      firstFieldRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      title: trimmed,
      summary: summary.trim() || null,
      category: category || null,
      status,
      priority,
      ourPosition,
      ourPositionNote: ourPositionNote.trim() || null,
      decisionDate: decisionDate || null,
      ownerId: ownerId || null,
    };
    try {
      const saved = issue ? await updateIssue(issue.id, payload) : await createIssue(payload);
      onSaved(saved);
    } catch (err) {
      setError(issuesErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy={titleId} handle initialFocusRef={firstFieldRef} className="max-h-[92dvh] sm:max-w-lg">
      <DialogHeader id={titleId} title={editing ? 'Edit issue' : 'New issue'} onClose={onClose} />
      <DialogBody className="space-y-3">
        <Field label="Title" htmlFor={`${titleId}-title`} required>
          <Input
            id={`${titleId}-title`}
            ref={firstFieldRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bauxite permit renewal, Boké"
            maxLength={300}
          />
        </Field>

        <Field label="What is at stake" htmlFor={`${titleId}-summary`}>
          <Textarea
            id={`${titleId}-summary`}
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="A few lines an outsider could follow"
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Category"
            htmlFor={`${titleId}-category`}
            hint={options.length ? undefined : 'Add categories under Categories → Issues'}
          >
            <Select
              id={`${titleId}-category`}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">No category</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Priority" htmlFor={`${titleId}-priority`}>
            <Select
              id={`${titleId}-priority`}
              value={String(priority)}
              onChange={(e) => setPriority(Number(e.target.value))}
            >
              {[5, 4, 3, 2, 1].map((value) => (
                <option key={value} value={value}>
                  {value} · {PRIORITY_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Decision date" htmlFor={`${titleId}-date`} hint="Leave blank when it is not known">
            <Input
              id={`${titleId}-date`}
              type="date"
              value={decisionDate}
              onChange={(e) => setDecisionDate(e.target.value)}
            />
          </Field>

          <Field label="Owner" htmlFor={`${titleId}-owner`}>
            <Select id={`${titleId}-owner`} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">Unassigned</option>
              {ownerOptions.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </Select>
          </Field>

          {editing && (
            <Field label="Status" htmlFor={`${titleId}-status`}>
              <Select
                id={`${titleId}-status`}
                value={status}
                onChange={(e) => setStatus(e.target.value as IssueStatus)}
              >
                {ISSUE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {STATUS_LABEL[value]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        <Field label="Our position" hint="What we want the outcome to be">
          <PositionPicker value={ourPosition} onChange={setOurPosition} label="Our position" />
        </Field>

        <Field label="Our line" htmlFor={`${titleId}-note`}>
          <Input
            id={`${titleId}-note`}
            value={ourPositionNote}
            onChange={(e) => setOurPositionNote(e.target.value)}
            placeholder="One line on why"
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" loading={saving} onClick={save}>
          {editing ? 'Save' : 'Create issue'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default IssueFormDialog;
